import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { vacationRequestService } from '../../services/vacationRequestService';
import { VACATION_DP_BOARD_QUERY_KEY } from './useVacationDpBoardQuery';
import { VACATION_APPROVALS_QUERY_KEY } from './usePendingApprovalsQuery';
import { VACATION_REQUESTS_QUERY_KEY } from './useMyVacationRequestsQuery';
import { VACATION_BALANCE_QUERY_KEY } from './useVacationBalanceQuery';

export interface DpDecisionInput {
    requestId: string;
    segmentDecisions: Array<{ order: number; kind: 'approve' | 'reject'; note: string }>;
    dpEmail: string;
    sellDecision?: 'approve' | 'reject';
    collaboratorId: string;
    collaboratorEmail: string;
}

const DECISION_TOASTS = {
    approve: {
        title: 'Solicitação validada',
        description: 'As férias foram agendadas e o colaborador será avisado.',
    },
    reject: {
        title: 'Solicitação reprovada',
        description: 'A reprovação foi registrada. O colaborador pode enviar uma nova solicitação.',
    },
    pending_correction: {
        title: 'Correção pendente',
        description: 'Aguardando ajuste do colaborador.',
    },
} as const;

export function useVacationDpMutations() {
    const queryClient = useQueryClient();
    const toast = useToast();

    const decideAsDp = useMutation({
        retry: false,
        mutationFn: (input: DpDecisionInput) =>
            vacationRequestService.decideAsDp({
                requestId: input.requestId,
                segmentDecisions: input.segmentDecisions,
                dpEmail: input.dpEmail,
                sellDecision: input.sellDecision,
            }),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [VACATION_DP_BOARD_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [VACATION_APPROVALS_QUERY_KEY] });
            queryClient.invalidateQueries({
                queryKey: [VACATION_REQUESTS_QUERY_KEY, variables.collaboratorEmail],
            });
            queryClient.invalidateQueries({
                queryKey: [VACATION_BALANCE_QUERY_KEY, variables.collaboratorId],
            });

            const allApproved = variables.segmentDecisions?.every((sd) => sd.kind === 'approve');
            const allRejected = variables.segmentDecisions?.every((sd) => sd.kind === 'reject');
            const toastKey = allRejected ? 'reject' : allApproved ? 'approve' : 'pending_correction';
            const copy = DECISION_TOASTS[toastKey as keyof typeof DECISION_TOASTS];
            toast.success(copy.title, copy.description);
        },
        onError: (error: Error) => {
            console.error('Erro ao registrar decisão do DP:', error);
            toast.error(
                'Não foi possível registrar a decisão',
                error.message || 'Verifique sua conexão e tente novamente.'
            );
        },
    });

    return { decideAsDp };
}
